import React, { useEffect, useState } from 'react';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Upload,
  Checkbox,
  Tooltip,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FormLayout } from 'antd/es/form/Form';
import { FormField } from '../../types';
import { useFormStore } from '../../store';
import { cn } from '../../lib/utils';
import { NumericInput } from './NumericInput';
import { SwitchInput } from './SwitchInput';
import StatusTag from './StatusTag';

const { TextArea } = Input;
const { Dragger } = Upload;
const { RangePicker } = DatePicker;

interface CustomFormProps {
  formFields: FormField[];
  form: any;
  formName: string;
  onFinish: (values: any) => void;
  onValuesChange?: (changedFields: any, allFields: any) => void;
  layout: FormLayout | undefined;
  rowCol?: number; // Optional prop for number of columns per row
  isLoading?: boolean;
  data: any;
  onDeleteFile?: (file: any) => void;
}

const CustomForm: React.FC<CustomFormProps> = ({
  formFields,
  form,
  formName,
  data,
  onFinish,
  onValuesChange,
  rowCol = 1, // Default to 1 columns per row if not provided
  layout,
  isLoading = false,
  onDeleteFile,
}) => {
  const formData = useFormStore((state) => state.formData);
  const setFormData = useFormStore((state) => state.setFormData);
  const resetForm = useFormStore((state) => state.resetForm);
  const [fileList, setFileList] = useState<any[]>([]);

  const filterOption = (
    input: string,
    option?: {
      label: string;
      value: string;
    },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const calculateColSpan = () => {
    return Math.floor(24 / rowCol); // Calculate the colSpan based on number of columns per row
  };

  useEffect(() => {
    if (data != null) {
      const defaultValues: any = {};
      formFields.forEach((field) => {
        if (field.type === 'datepicker' && data[field.name]) {
          defaultValues[field.name] = dayjs(data[field.name]);
        } else if (field.type === 'date-range-picker' && data[field.name]) {
          const [startDate, endDate] = data[field.name];
          defaultValues[field.name] = [dayjs(startDate), dayjs(endDate)];
        } else if (field.type === 'file' && data[field.name]) {
          // Handle default value for file upload
          setFileList(data[field.name]);
        } else {
          defaultValues[field.name] =
            field.defaultValue !== undefined
              ? field.defaultValue
              : data[field.name];
        }
      });
      form.setFieldsValue(defaultValues);
    }
  }, [form]);

  useEffect(() => {
    return () => {
      resetForm(); // Reset form data when unmounting
    };
  }, [resetForm]); // Dependency on resetForm

  const handleFileRemove = (file: any) => {
    // Call onDeleteFile callback with the file to be deleted
    if (onDeleteFile) {
      onDeleteFile(file);
    }

    const newFileList = fileList.filter((f: any) => f.uid !== file.uid);
    setFileList(newFileList);
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    if (info.file.status === 'done') {
      // If a new file is successfully uploaded, keep the existing files
      newFileList = newFileList.filter((file: any) => !!file.url);
    }
    setFileList(newFileList);
    if (info.file.status === 'done') {
      // Update form data with file information
      setFormData({ ...formData, [formFields[0].name]: newFileList });
    }
  };

  return (
    <Form
      name={formName}
      form={form}
      layout={layout}
      onFinish={onFinish}
      onValuesChange={onValuesChange}
    >
      <Row gutter={16}>
        {formFields.map((field) => (
          <Col key={field.name} span={calculateColSpan()}>
            {field.values ? (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={field.rules}
              >
                {field.type === 'select' ? (
                  field.isMultiple ? (
                    <Select
                      disabled={field.disabled}
                      showSearch
                      mode={'multiple'}
                      placeholder={`Please select ${field.label}`}
                      style={{ width: '100%' }}
                      filterOption={filterOption}
                      loading={isLoading}
                      options={field.values.map((item) => ({
                        value: item.id,
                        label: `${item.code} - ${item.description}`,
                      }))}
                    />
                  ) : (
                    <Select
                      disabled={field.disabled}
                      showSearch
                      allowClear={field.allowClear}
                      placeholder={`Please select ${field.label}`}
                      style={{ width: '100%' }}
                      filterOption={filterOption}
                      loading={isLoading}
                      options={field.values.map((item) => ({
                        value: item.id,
                        label: `${item.code} - ${item.description}`,
                      }))}
                    />
                  )
                ) : field.type === 'radio-group-button' ? (
                  <Radio.Group>
                    {field.values.map((option) => (
                      <Radio.Button key={option.id} value={option.id}>
                        {option.description}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                ) : (
                  <Radio.Group>
                    {field.values.map((option) => (
                      <Radio key={option.id} value={option.id}>
                        {option.description}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              </Form.Item>
            ) : field.type === 'file' ? (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={field.rules}
              >
                <Dragger
                  fileList={fileList}
                  onChange={handleFileChange}
                  onRemove={handleFileRemove}
                  beforeUpload={() => false} // Disable automatic file upload
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              </Form.Item>
            ) : field.type === 'password' ? (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={field.rules}
              >
                <Input.Password
                  placeholder={`Please enter ${field.label}`}
                  disabled={field.disabled}
                  value={formData[field.name]}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [field.name]: e.target.value,
                    })
                  }
                />
              </Form.Item>
            ) : field.type === 'confirmPassword' ? (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={[
                  {
                    required: true,
                    message: `Please confirm your ${field.label}`,
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value ||
                        getFieldValue('password') === value ||
                        getFieldValue('newPassword') === value
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('The two passwords do not match'),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder={`Please confirm your ${field.label}`}
                  disabled={field.disabled}
                  value={formData[field.name]}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [field.name]: e.target.value,
                    })
                  }
                />
              </Form.Item>
            ) : field.type === 'checkbox-group' ? (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={field.rules}
              >
                <Checkbox.Group options={field.options} />
              </Form.Item>
            ) : (
              <Form.Item
                label={field.label}
                name={field.name}
                rules={field.rules}
                className={cn(field.name === 'status' ? 'w-[5rem]' : '')}
              >
                {field.type === 'numeric' ? (
                  <NumericInput
                    placeholder={`Please enter ${field.label}`}
                    disabled={field.disabled}
                    value={formData[field.name]}
                    onChange={(value: string) =>
                      setFormData({ ...formData, [field.name]: value })
                    }
                  />
                ) : field.type === 'textarea' ? (
                  <TextArea
                    placeholder={`Please enter ${field.label}`}
                    disabled={field.disabled}
                    value={formData[field.name]}
                  />
                ) : field.type === 'switch' ? (
                  <SwitchInput
                    checked={formData[field.name]}
                    onChange={(value: boolean) =>
                      setFormData({ ...formData, [field.name]: value })
                    }
                    disabled={field.disabled}
                  />
                ) : field.type === 'datepicker' ? (
                  <DatePicker
                    disabled={field.disabled}
                    style={{ width: '100%' }}
                    value={formData[field.name]}
                    onChange={(date) =>
                      setFormData({ ...formData, [field.name]: date })
                    }
                  />
                ) : field.type === 'date-range-picker' ? (
                  <RangePicker
                    style={{ width: '100%' }}
                    disabled={field.disabled}
                    value={formData[field.name]}
                    onChange={(dates) =>
                      setFormData({ ...formData, [field.name]: dates })
                    }
                  />
                ) : field.type === 'status' ? (
                  <StatusTag status={form.getFieldValue(field.name)} />
                ) : (
                  <Input
                    placeholder={`Please enter ${field.label}`}
                    disabled={field.disabled}
                    value={formData[field.name]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        [field.name]: e.target.value,
                      })
                    }
                  />
                )}
              </Form.Item>
            )}
          </Col>
        ))}
      </Row>
    </Form>
  );
};

export default CustomForm;
